import { loadStripe as loadStripeSDK } from '@stripe/stripe-js';
import { logger } from './logger';
import api from './api';
import { PAYMENT_TIERS, getTierById, isPaidTier } from './paymentTiers';
import { extractApiErrorMessage } from './errorUtils';
import type { PaymentTier } from './paymentTiers';

// Re-export from paymentTiers for backwards compatibility
export { PAYMENT_TIERS, getTierById, isPaidTier };
export type { PaymentTier };

/**
 * Stripe Service - Payment Processing for Legacy Catalog
 * Uses managed API client for automatic JWT token handling, proactive token refresh,
 * and consistent error handling
 */

// Stripe configuration cache
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let stripePromise: Promise<unknown> | null = null;
let stripeConfig: {
  publishableKey: string;
  creatorMonthlyPriceId: string;
  creatorYearlyPriceId: string;
  commercialMonthlyPriceId: string;
  commercialYearlyPriceId: string;
} | null = null;
let configLoaded = false;

// Immutable price ID map populated from API config
let priceIdMap: Record<string, { monthlyPriceId: string; yearlyPriceId: string }> = {};

export interface CreateCheckoutSessionRequest {
  tier: 'creator' | 'commercial';
  userId: string;
  signupData: {
    firstName: string;
    lastName: string;
    password: string;
    organization?: string;
  };
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  sessionId: string;
  subscriptionId?: string;
  userId: string;
  tier: string;
  paymentStatus: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  organization?: string;
}

export async function loadStripeConfig(): Promise<void> {
  if (configLoaded) return;

  try {
    // Use managed API client (automatic token handling)
    const response = await api.get('/api/payment/config');
    stripeConfig = response.data;

    // Initialize Stripe with the publishable key
    stripePromise = loadStripeSDK(stripeConfig.publishableKey);

    // Build immutable price ID map from API config (no mutation of PAYMENT_TIERS)
    priceIdMap = {
      creator: {
        monthlyPriceId: stripeConfig.creatorMonthlyPriceId,
        yearlyPriceId: stripeConfig.creatorYearlyPriceId,
      },
      commercial: {
        monthlyPriceId: stripeConfig.commercialMonthlyPriceId,
        yearlyPriceId: stripeConfig.commercialYearlyPriceId,
      },
    };

    configLoaded = true;
  } catch (error: unknown) {
    const err = error as Error;
    logger.error('user-activity-user-friendly: Payment system setup failed');
    logger.error('user-activity-tech-friendly: Stripe config load error', {
      error: err.message,
      errorType: err.name
    });
    throw error;
  }
}

export async function createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CheckoutSessionResponse> {
  await loadStripeConfig();

  try {
    // Use managed API client (automatic token handling)
    const response = await api.post('/api/payment/checkout', request);

    return response.data;
  } catch (error: unknown) {
    // axios error handling — detail may be string (HTTPException) or array (Pydantic 422)
    const err = error as { response?: { status: number; data: { detail?: unknown; error?: string } } };
    const rawDetail = err.response?.data?.detail;
    const errorMessage = rawDetail !== undefined
      ? extractApiErrorMessage(err.response?.data, err.response?.status ?? 0)
      : err.response?.data?.error ?? 'Failed to create checkout session';

    logger.error('user-activity-user-friendly: Payment session creation failed', {
      user_tier: request.tier,
      email: request.userId,
      error: errorMessage
    });
    logger.error('user-activity-tech-friendly: Checkout session API error', {
      status: err.response?.status,
      error: errorMessage,
      request
    });

    throw new Error(errorMessage);
  }
}

export async function redirectToCheckout(checkoutUrl: string): Promise<void> {
  if (!checkoutUrl) {
    logger.error('user-activity-user-friendly: Payment redirect failed - no URL provided');
    logger.error('user-activity-tech-friendly: Missing checkout URL');
    throw new Error('No checkout URL provided');
  }

  // Redirect directly to the checkout URL provided by Stripe
  // This replaces the deprecated stripe.redirectToCheckout() method
  window.location.href = checkoutUrl;
}

export async function verifyPayment(sessionId: string): Promise<PaymentVerificationResponse> {
  await loadStripeConfig();

  try {
    // Use managed API client (automatic token handling)
    const response = await api.get('/api/payment/verify', {
      params: { session_id: sessionId }
    });

    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status: number; data: { error?: string } } };
    const errorMessage = err.response?.data?.error || 'Failed to verify payment';

    logger.error('user-activity-user-friendly: Payment verification failed', {
      session_id: sessionId?.substring(0, 20) + '...',
      error: errorMessage
    });
    logger.error('user-activity-tech-friendly: Payment verification API error', {
      status: err.response?.status,
      error: errorMessage,
      sessionId
    });

    throw new Error(errorMessage);
  }
}

// Get price IDs for a tier (loaded from API config)
export function getPriceIds(tierId: string): { monthlyPriceId: string; yearlyPriceId: string } | undefined {
  return priceIdMap[tierId];
}

// Test helper to reset module state
export function resetStripeConfig(): void {
  configLoaded = false;
  stripeConfig = null;
  stripePromise = null;
  priceIdMap = {};
}
